import React, { useState, useCallback, useContext, useEffect } from "react";
import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import {
	Button,
	Box,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	InputAdornment,
	Grid,
	TableBody,
	TableRow,
	TableCell,
	IconButton,
	Table,
	TableHead,
	Paper,
	Tooltip,
	Typography,
	CircularProgress,
	TextField,
	Menu,
	MenuItem,
	ListItemIcon,
	ListItemText,
} from "@material-ui/core";
import {
	Add,
	ArrowBack,
	Edit,
	SyncAlt,
	Search,
	FilterList,
	Business,
	MoreVert,
	CheckCircle,
	Visibility,
	ArchiveOutlined,
	UnarchiveOutlined,
	Phone,
	WhatsApp,
	Telegram,
	Instagram,
	Facebook,
	Email,
	Sms,
	Chat,
	Forum,
	ReportProblem,
	SignalCellularConnectedNoInternet2Bar,
	SignalCellularConnectedNoInternet0Bar,
	SignalCellular4Bar,
	CropFree,
	DeleteOutline,
	PowerSettingsNew,
	NetworkCheck,
} from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";

import api from "../../services/api";
import WhatsAppModal from "../../components/WhatsAppModal";
import ConnectivityTestDialog from "../../components/ConnectivityTestDialog";
import ConfirmationModal from "../../components/ConfirmationModal";
import QrcodeModal from "../../components/QrcodeModal";
import { i18n } from "../../translate/i18n";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
	pageSurface: {
		flex: 1,
		minHeight: 0,
		overflowY: "auto",
		padding: theme.spacing(1, 0.5, 2),
		...theme.scrollbarStyles,
	},
	mainPaper: {
		flex: 1,
		padding: theme.spacing(1),
		overflowY: "scroll",
		...theme.scrollbarStyles,
	},
	planPaper: {
		padding: theme.spacing(0),
		marginBottom: theme.spacing(2),
	},
	connectionsToolbar: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: theme.spacing(2),
		flexWrap: "wrap",
		marginBottom: theme.spacing(2),
	},
	connectionsActions: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1),
		flexWrap: "wrap",
	},
	searchField: {
		minWidth: 320,
		[theme.breakpoints.down("xs")]: {
			minWidth: "100%",
		},
	},
	tabsRow: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(2),
		borderBottom: `1px solid ${theme.palette.divider}`,
		marginBottom: theme.spacing(2),
	},
	tabItem: {
		border: 0,
		background: "transparent",
		padding: theme.spacing(1.25, 0),
		fontWeight: 700,
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1),
		borderBottom: `2px solid transparent`,
		color: theme.palette.text.secondary,
		cursor: "pointer",
		fontFamily: "inherit",
	},
	tabItemActive: {
		color: theme.palette.text.primary,
		borderBottomColor: theme.palette.primary.main,
	},
	countBubble: {
		minWidth: 26,
		height: 26,
		padding: theme.spacing(0, 1),
		borderRadius: 999,
		background: theme.palette.type === "dark" ? "#1f2937" : "#e5e7eb",
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		fontSize: 13,
	},
	connectionCardsGrid: {
		marginTop: theme.spacing(1),
	},
	activeConnectionCard: {
		borderRadius: 14,
		border: `1px solid ${theme.palette.divider}`,
		background: theme.palette.background.paper,
		overflow: "hidden",
		height: "100%",
		boxShadow:
			theme.palette.type === "dark"
				? "0 12px 28px rgba(0,0,0,0.24)"
				: "0 12px 28px rgba(15,23,42,0.06)",
	},
	activeConnectionHeader: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: theme.spacing(1.5),
		padding: theme.spacing(1.75, 2),
		borderBottom: `1px solid ${theme.palette.divider}`,
	},
	activeConnectionTitle: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1.5),
		minWidth: 0,
	},
	activeConnectionHeaderActions: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(0.75),
		flexShrink: 0,
	},
	activeConnectionBody: {
		padding: theme.spacing(2),
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing(1.35),
	},
	connectButton: {
		borderRadius: 999,
		textTransform: "none",
		fontWeight: 800,
		padding: theme.spacing(0.6, 1.5),
	},
	connectButtonConnected: {
		"&.Mui-disabled": {
			color: theme.palette.type === "dark" ? "#bbf7d0" : "#15803d",
			borderColor:
				theme.palette.type === "dark"
					? "rgba(34,197,94,0.45)"
					: "rgba(34,197,94,0.35)",
			backgroundColor:
				theme.palette.type === "dark"
					? "rgba(34,197,94,0.14)"
					: "rgba(220,252,231,0.92)",
		},
	},
	cardMetaRow: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1.25),
		color: theme.palette.text.secondary,
	},
	cardMetaText: {
		minWidth: 0,
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
	},
	statusPill: {
		alignSelf: "flex-start",
		borderRadius: 999,
		border: `1px solid ${theme.palette.divider}`,
		padding: theme.spacing(0.25, 1),
		fontSize: 12,
		fontWeight: 700,
		display: "inline-flex",
		alignItems: "center",
		gap: theme.spacing(0.75),
	},
	statusDot: {
		width: 8,
		height: 8,
		borderRadius: "50%",
		background: "#ef4444",
	},
	connectionMenuPaper: {
		borderRadius: 12,
		boxShadow:
			theme.palette.type === "dark"
				? "0 16px 40px rgba(0,0,0,0.45)"
				: "0 16px 40px rgba(15,23,42,0.14)",
	},
	connectionMenuDanger: {
		color: "#b91c1c",
		"& svg": {
			color: "#ef4444",
		},
		"&:hover": {
			backgroundColor:
				theme.palette.type === "dark" ? "rgba(239,68,68,0.12)" : "#fff1f2",
		},
	},
	paginationRow: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		marginTop: theme.spacing(2),
		color: theme.palette.text.secondary,
	},
	addPageHeader: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1.5),
		marginBottom: theme.spacing(3),
	},
	breadcrumb: {
		color: theme.palette.text.secondary,
		fontSize: 14,
		marginBottom: theme.spacing(1.5),
	},
	addLayout: {
		maxWidth: 980,
		margin: "0 auto",
	},
	planHeader: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: theme.spacing(2),
		flexWrap: "wrap",
		marginBottom: theme.spacing(1),
	},
	customTableCell: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
	},
	tooltip: {
		backgroundColor: "#f5f5f9",
		color: "rgba(0, 0, 0, 0.87)",
		fontSize: theme.typography.pxToRem(14),
		border: "1px solid #dadde9",
		maxWidth: 450,
	},
	tooltipPopper: {
		textAlign: "center",
	},
	buttonProgress: {
		color: green[500],
	},
	connectionGrid: {
		paddingTop: theme.spacing(1),
		justifyContent: "center",
	},
	channelCard: {
		height: "100%",
		minHeight: 188,
		padding: theme.spacing(2.5),
		borderRadius: 14,
		border: `1px solid ${theme.palette.divider}`,
		backgroundColor: theme.palette.background.paper,
		display: "flex",
		flexDirection: "column",
		justifyContent: "flex-start",
		cursor: "pointer",
		transition: "border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease",
		"&:hover": {
			borderColor: theme.palette.primary.main,
			boxShadow:
				theme.palette.type === "dark"
					? "0 14px 32px rgba(0,0,0,0.35)"
					: "0 14px 32px rgba(15, 23, 42, 0.12)",
			transform: "translateY(-1px)",
		},
	},
	channelCardDisabled: {
		opacity: theme.palette.type === "dark" ? 0.62 : 0.68,
		filter: theme.palette.type === "dark" ? "grayscale(0.25)" : "grayscale(0.55)",
		"&:hover": {
			borderColor: theme.palette.primary.main,
			boxShadow:
				theme.palette.type === "dark"
					? "0 14px 32px rgba(0,0,0,0.28)"
					: "0 14px 32px rgba(15, 23, 42, 0.08)",
			transform: "translateY(-1px)",
		},
	},
	channelIcon: {
		width: 42,
		height: 42,
		borderRadius: "50%",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		fontWeight: 800,
		color: "#fff",
		marginBottom: theme.spacing(2),
		"& svg": {
			fontSize: 24,
		},
	},
	channelIconDisabled: {
		background: `${theme.palette.type === "dark" ? "#334155" : "#e5e7eb"} !important`,
		color: theme.palette.type === "dark" ? "#94a3b8" : "#64748b",
	},
	channelHeader: {
		display: "flex",
		alignItems: "flex-start",
		justifyContent: "space-between",
		gap: theme.spacing(1),
		marginBottom: theme.spacing(1.5),
	},
	channelStatus: {
		display: "inline-flex",
		alignItems: "center",
		gap: theme.spacing(0.75),
		color: theme.palette.text.secondary,
		fontSize: 14,
	},
	channelStatusDot: {
		width: 8,
		height: 8,
		borderRadius: "50%",
		background: "#ef4444",
	},
	channelName: {
		fontWeight: 700,
		fontSize: 16,
		marginBottom: theme.spacing(0.5),
	},
	channelDescription: {
		color: theme.palette.text.secondary,
		fontSize: 13,
		lineHeight: 1.45,
	},
	channelFooter: {
		marginTop: "auto",
		paddingTop: theme.spacing(2),
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: theme.spacing(1),
	},
	channelActionButton: {
		borderRadius: 999,
		textTransform: "none",
		fontWeight: 800,
		padding: theme.spacing(0.5, 1.4),
	},
	connectionList: {
		marginTop: theme.spacing(1.25),
		paddingTop: theme.spacing(1),
		borderTop: `1px solid ${theme.palette.divider}`,
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing(0.75),
	},
	connectionRow: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: theme.spacing(1),
		fontSize: 12,
		color: theme.palette.text.secondary,
	},
	connectionNameInline: {
		minWidth: 0,
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
		fontWeight: 700,
		color: theme.palette.text.primary,
	},
}));

const channelVisuals = {
	baileys: { icon: WhatsApp, color: "#25D366" },
	waba: { icon: WhatsApp, color: "#128C7E" },
	webchat: { icon: Chat, color: "#4f7df3" },
	telegram: { icon: Telegram, color: "#229ED9" },
	instagram: { icon: Instagram, color: "#E1306C" },
	messenger: { icon: Facebook, color: "#0084FF" },
	email: { icon: Email, color: "#64748b" },
	sms: { icon: Sms, color: "#475569" },
	reclameAqui: { icon: ReportProblem, color: "#94a3b8" },
};

const supportedConnectionTypes = [
	"baileys",
	"waba",
	"webchat",
	"instagram",
	"messenger",
];
const standaloneConnectionTypes = [
	"waba",
	"gupshup",
	"webchat",
	"instagram",
	"messenger",
];

const CustomToolTip = ({ title, content, children }) => {
	const classes = useStyles();

	return (
		<Tooltip
			arrow
			classes={{
				tooltip: classes.tooltip,
				popper: classes.tooltipPopper,
			}}
			title={
				<React.Fragment>
					<Typography gutterBottom color="inherit">
						{title}
					</Typography>
					{content && <Typography>{content}</Typography>}
				</React.Fragment>
			}
		>
			{children}
		</Tooltip>
	);
};

const Connections = () => {
	const classes = useStyles();

	const { whatsApps, loading, refreshWhatsApps } = useContext(WhatsAppsContext);
	const [screenMode, setScreenMode] = useState("list");
	const [searchTerm, setSearchTerm] = useState("");
	const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
	const [connectivityTestOpen, setConnectivityTestOpen] = useState(false);
	const [connectionOptions, setConnectionOptions] = useState([]);
	const [connectionOptionsLoading, setConnectionOptionsLoading] = useState(false);
	const [supportWhatsappNumber, setSupportWhatsappNumber] = useState("");
	const [selectedConnectionType, setSelectedConnectionType] = useState("baileys");
	const [expandedChannelKey, setExpandedChannelKey] = useState("");
	const [sessionActionLoadingId, setSessionActionLoadingId] = useState(null);
	const [blockedChannelDialog, setBlockedChannelDialog] = useState({
		open: false,
		option: null,
	});
	const [qrModalOpen, setQrModalOpen] = useState(false);
	const [selectedWhatsApp, setSelectedWhatsApp] = useState(null);
	const [confirmModalOpen, setConfirmModalOpen] = useState(false);
	const [connectionsTab, setConnectionsTab] = useState("active");
	const [connectionMenuAnchor, setConnectionMenuAnchor] = useState(null);
	const [connectionMenuWhatsApp, setConnectionMenuWhatsApp] = useState(null);
	const confirmationModalInitialState = {
		action: "",
		title: "",
		message: "",
		whatsAppId: "",
		open: false,
	};
	const [confirmModalInfo, setConfirmModalInfo] = useState(
		confirmationModalInitialState
	);

	const handleStartWhatsAppSession = async whatsAppId => {
		setSessionActionLoadingId(whatsAppId);
		try {
			await api.post(`/whatsappsession/${whatsAppId}`);
			toast.success("Reconexão solicitada. Aguarde alguns segundos.");
			await refreshWhatsApps?.();
		} catch (err) {
			toastError(err);
		} finally {
			setSessionActionLoadingId(null);
		}
	};

	const handleRequestNewQrCode = async whatsAppId => {
		setSessionActionLoadingId(whatsAppId);
		try {
			await api.put(`/whatsappsession/${whatsAppId}`);
			toast.success("Novo QR Code solicitado.");
			await refreshWhatsApps?.();
		} catch (err) {
			toastError(err);
		} finally {
			setSessionActionLoadingId(null);
		}
	};

	const handleOpenWhatsAppModal = () => {
		setScreenMode("add");
		loadConnectionOptions();
	};

	const loadConnectionOptions = useCallback(async () => {
		setConnectionOptionsLoading(true);
		try {
			const { data } = await api.get("/whatsapp/connection-options");
			setConnectionOptions(Array.isArray(data?.options) ? data.options : []);
			setSupportWhatsappNumber(data?.supportWhatsappNumber || "");
		} catch (err) {
			toastError(err);
		} finally {
			setConnectionOptionsLoading(false);
		}
	}, []);

	useEffect(() => {
		loadConnectionOptions();
	}, [loadConnectionOptions]);

	const handleSelectConnectionOption = option => {
		if (!option || option.state !== "available" || !option.supported) {
			setBlockedChannelDialog({
				open: true,
				option,
			});
			return;
		}

		setSelectedWhatsApp(null);
		setSelectedConnectionType(option.key);
		setWhatsAppModalOpen(true);
	};

	const canCreateConnectionFromOption = option =>
		Boolean(
			option &&
				option.state === "available" &&
				option.supported &&
				(option.available === null || Number(option.available || 0) > 0)
		);

	const getConnectionsByChannel = option => {
		if (!option) {
			return [];
		}

		return (whatsApps || []).filter(whatsApp => {
			const type = whatsApp.connectionType || "baileys";
			if (standaloneConnectionTypes.includes(option.key)) {
				return type === option.key;
			}
			return option.key === "baileys" && !standaloneConnectionTypes.includes(type);
		});
	};

	const handleChannelCardClick = option => {
		const connections = getConnectionsByChannel(option);
		if (connections.length > 0) {
			setExpandedChannelKey(current =>
				current === option.key ? "" : option.key
			);
			return;
		}

		handleSelectConnectionOption(option);
	};

	const handleCloseBlockedChannelDialog = () => {
		setBlockedChannelDialog({
			open: false,
			option: null,
		});
	};

	const handleContactSupport = () => {
		const option = blockedChannelDialog.option;
		const supportNumber = String(supportWhatsappNumber || "").replace(/\D/g, "");
		if (supportNumber) {
			const text = encodeURIComponent(
				`Ola, gostaria de habilitar o canal ${option?.name || ""} no meu plano.`
			);
			window.open(`https://wa.me/${supportNumber}?text=${text}`, "_blank");
			handleCloseBlockedChannelDialog();
			return;
		}
		const subject = encodeURIComponent(
			`Solicitar canal ${option?.name || "adicional"}`
		);
		const body = encodeURIComponent(
			`Ola, gostaria de habilitar o canal ${option?.name || ""} no meu plano.`
		);
		window.location.href = `mailto:suporte@deskadvisor.io?subject=${subject}&body=${body}`;
		handleCloseBlockedChannelDialog();
	};

	const handleCloseWhatsAppModal = useCallback(() => {
		setWhatsAppModalOpen(false);
		setSelectedWhatsApp(null);
		setSelectedConnectionType("baileys");
		setScreenMode("list");
		loadConnectionOptions();
	}, [setSelectedWhatsApp, setWhatsAppModalOpen, loadConnectionOptions]);

	const handleOpenQrModal = whatsApp => {
		setSelectedWhatsApp(whatsApp);
		setQrModalOpen(true);
	};

	const handleCloseQrModal = useCallback(() => {
		setSelectedWhatsApp(null);
		setQrModalOpen(false);
	}, [setQrModalOpen, setSelectedWhatsApp]);

	const handleEditWhatsApp = whatsApp => {
		setSelectedWhatsApp(whatsApp);
		setSelectedConnectionType(whatsApp?.connectionType || "baileys");
		setWhatsAppModalOpen(true);
	};

	const handleOpenConnectionMenu = (event, whatsApp) => {
		event.stopPropagation();
		setConnectionMenuAnchor(event.currentTarget);
		setConnectionMenuWhatsApp(whatsApp);
	};

	const handleCloseConnectionMenu = () => {
		setConnectionMenuAnchor(null);
		setConnectionMenuWhatsApp(null);
	};

	const normalizeStatus = status => String(status || "").toUpperCase();

	const isConnectedStatus = status => normalizeStatus(status) === "CONNECTED";

	const isConnectingStatus = status =>
		["OPENING", "PAIRING"].includes(normalizeStatus(status));

	const isWabaConnection = whatsApp =>
		String(whatsApp?.connectionType || "").toLowerCase() === "waba";

	const isMetaConnection = whatsApp =>
		["instagram", "messenger"].includes(
			String(whatsApp?.connectionType || "").toLowerCase()
		);

	const isGupshupConnection = whatsApp =>
		String(whatsApp?.connectionType || "").toLowerCase() === "gupshup";

	// Nao ha sessao para abrir nem QR para ler: a conexao esta pronta assim que
	// tem app, numero e chave. Sem isto ela apareceria eternamente "conectando".
	const isGupshupConfigured = whatsApp =>
		isGupshupConnection(whatsApp) &&
		Boolean(String(whatsApp?.gupshupAppName || "").trim()) &&
		Boolean(String(whatsApp?.connectedNumber || "").trim());

	const isQrCodeWhatsAppConnection = whatsApp => {
		const type = String(whatsApp?.connectionType || "baileys").toLowerCase();
		return type === "baileys" || !standaloneConnectionTypes.includes(type);
	};

	const isWabaConfigured = whatsApp =>
		isWabaConnection(whatsApp) &&
		Boolean(whatsApp?.wabaEnabled) &&
		Boolean(String(whatsApp?.wabaPhoneNumberId || "").trim());

	const isMetaConfigured = whatsApp =>
		isMetaConnection(whatsApp) &&
		(Boolean(whatsApp?.hasMetaAccessToken) ||
			Boolean(String(whatsApp?.metaAccessToken || "").trim())) &&
		Boolean(
			String(
				whatsApp?.connectionType === "instagram"
					? whatsApp?.metaInstagramBusinessId
					: whatsApp?.metaPageId
			).trim()
		);

	const isConnectionConnected = whatsApp =>
		isWabaConnection(whatsApp)
			? isWabaConfigured(whatsApp)
			: isGupshupConnection(whatsApp)
			? isGupshupConfigured(whatsApp)
			: isMetaConnection(whatsApp)
			? isMetaConfigured(whatsApp)
			: isConnectedStatus(whatsApp?.status);

	const isConnectionConnecting = whatsApp =>
		!isWabaConnection(whatsApp) &&
		!isGupshupConnection(whatsApp) &&
		!isMetaConnection(whatsApp) &&
		isConnectingStatus(whatsApp?.status);

	const isQrConnectionOpening = whatsApp =>
		isQrCodeWhatsAppConnection(whatsApp) &&
		["OPENING", "PAIRING", "TIMEOUT", "DISCONNECTED", "QRCODE"].includes(
			normalizeStatus(whatsApp?.status)
		);

	const handleConnectWhatsApp = whatsApp => {
		if (isWabaConnection(whatsApp) || isMetaConnection(whatsApp)) {
			handleEditWhatsApp(whatsApp);
			return;
		}
		const status = normalizeStatus(whatsApp?.status);
		if (whatsApp?.status === "qrcode" || status === "QRCODE") {
			handleOpenQrModal(whatsApp);
			return;
		}
		handleStartWhatsAppSession(whatsApp.id);
	};

	const getConnectLabel = whatsApp => {
		if (isWabaConnection(whatsApp)) {
			return isWabaConfigured(whatsApp) ? i18n.t("connections.extras.configured") : i18n.t("connections.extras.configure");
		}
		if (isMetaConnection(whatsApp)) {
			return isMetaConfigured(whatsApp) ? i18n.t("connections.extras.configured") : i18n.t("connections.extras.configure");
		}
		if (isConnectionConnected(whatsApp)) return i18n.t("connections.extras.connected");
		if (normalizeStatus(whatsApp?.status) === "OPENING") return i18n.t("connections.extras.connecting");
		if (["PAIRING", "TIMEOUT", "DISCONNECTED"].includes(normalizeStatus(whatsApp?.status))) {
			return i18n.t("connections.extras.reconnect");
		}
		return i18n.t("connections.extras.connect");
	};

	const formatConnectionPhone = value => {
		const raw = String(value || "").trim();
		if (!raw) return "";
		const jidLike = raw.split("@")[0].split(":")[0];
		const normalizedRaw = jidLike || raw;
		const digits = normalizedRaw.replace(/\D/g, "");
		if (!digits || digits.length < 8) return raw;
		if (digits.length === 13 && digits.startsWith("55")) {
			return `+${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(4, 9)} ${digits.slice(9)}`;
		}
		if (digits.length === 12 && digits.startsWith("55")) {
			return `+${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(4, 8)} ${digits.slice(8)}`;
		}
		return raw;
	};

	const getSessionPhone = session => {
		if (!session) return "";
		try {
			const parsed = typeof session === "string" ? JSON.parse(session) : session;
			return (
				parsed?.creds?.me?.id ||
				parsed?.me?.id ||
				parsed?.user?.id ||
				parsed?.id ||
				""
			);
		} catch (err) {
			return "";
		}
	};

	const getConnectionPhone = whatsApp => {
		if (isWabaConnection(whatsApp)) {
			const displayNumber = [
				whatsApp?.connectedNumber,
				whatsApp?.connectedWid,
				whatsApp?.wabaPhoneNumber,
				whatsApp?.phone,
			].find(item => String(item || "").trim());

			if (displayNumber) return formatConnectionPhone(displayNumber);
			if (String(whatsApp?.wabaPhoneNumberId || "").trim()) {
				return `Phone Number ID ${whatsApp.wabaPhoneNumberId}`;
			}
			return i18n.t("connections.extras.didMissing");
		}

		if (isMetaConnection(whatsApp)) {
			const type = String(whatsApp?.connectionType || "").toLowerCase();
			const id =
				type === "instagram"
					? whatsApp?.metaInstagramBusinessId
					: whatsApp?.metaPageId;
			return id
				? `${type === "instagram" ? "IG ID" : "Page ID"} ${id}`
				: i18n.t("connections.extras.metaIdMissing");
		}

		const candidates = [
			whatsApp?.connectedNumber,
			whatsApp?.connectedWid,
			whatsApp?.phoneNumber,
			whatsApp?.number,
			whatsApp?.wabaPhoneNumber,
			whatsApp?.phone,
			whatsApp?.whatsappNumber,
			getSessionPhone(whatsApp?.session),
		];
		const value = candidates.find(item => String(item || "").trim());
		return formatConnectionPhone(value) || i18n.t("connections.extras.numberMissing");
	};

	const getConnectionVisual = whatsApp => {
		const type = whatsApp.connectionType || "baileys";
		const key = standaloneConnectionTypes.includes(type) ? type : "baileys";
		return channelVisuals[key] || channelVisuals.baileys;
	};

	const getConnectionTypeLabel = whatsApp => {
		const type = String(whatsApp?.connectionType || "baileys").toLowerCase();
		if (type === "waba") return "WhatsApp Business API";
		if (type === "gupshup") return "Gupshup (WhatsApp)";
		if (type === "webchat") return "WebChat";
		if (type === "instagram") return "Instagram Direct";
		if (type === "messenger") return "Facebook Messenger";
		return "WhatsApp";
	};

	const getStatusLabel = status => {
		const normalized = String(status || "").toUpperCase();
		const labels = {
			CONNECTED: i18n.t("connections.extras.connected"),
			DISCONNECTED: i18n.t("connections.extras.disconnected"),
			OPENING: i18n.t("connections.extras.connecting"),
			PAIRING: i18n.t("connections.extras.pairing"),
			TIMEOUT: i18n.t("connections.extras.unstable"),
			WABA_CONFIG: i18n.t("connections.extras.configure"),
			GUPSHUP_CONFIG: i18n.t("connections.extras.configure"),
			META_CONFIG: i18n.t("connections.extras.configure"),
			WEBCHAT: "WebChat",
		};
		return labels[normalized] || status || "-";
	};

	const getConnectionStatusLabel = whatsApp => {
		if (isGupshupConnection(whatsApp)) {
			return isGupshupConfigured(whatsApp)
				? i18n.t("connections.extras.configured")
				: i18n.t("connections.extras.configure");
		}

		if (isWabaConnection(whatsApp)) {
			const wabaStatus = String(whatsApp?.wabaStatus || "").toLowerCase();
			if (wabaStatus === "connected") return i18n.t("connections.extras.connected");
			if (isWabaConfigured(whatsApp)) return i18n.t("connections.extras.configured");
			if (whatsApp?.wabaEnabled) return i18n.t("connections.extras.pending");
			return i18n.t("connections.extras.configure");
		}

		if (isMetaConnection(whatsApp)) {
			const metaStatus = String(whatsApp?.metaStatus || "").toLowerCase();
			if (metaStatus === "configured" || isMetaConfigured(whatsApp)) {
				return i18n.t("connections.extras.configured");
			}
			return i18n.t("connections.extras.configure");
		}

		return getStatusLabel(whatsApp?.status);
	};

	const filteredWhatsApps = (whatsApps || []).filter(whatsApp => {
		const term = searchTerm.trim().toLowerCase();
		if (!term) return true;
		return String(whatsApp.name || "").toLowerCase().includes(term);
	});

	const activeWhatsApps = filteredWhatsApps.filter(whatsApp => !whatsApp.archivedAt);
	const archivedWhatsApps = filteredWhatsApps.filter(whatsApp => Boolean(whatsApp.archivedAt));
	const visibleWhatsApps =
		connectionsTab === "archived" ? archivedWhatsApps : activeWhatsApps;

	const renderConnectionCards = () => (
		<div className={classes.pageSurface}>
			<div className={classes.connectionsToolbar}>
				<Title>{i18n.t("connections.title")}</Title>
				<div className={classes.connectionsActions}>
					<TextField
						className={classes.searchField}
						variant="outlined"
						size="small"
						placeholder={i18n.t("connections.extras.searchPlaceholder")}
						value={searchTerm}
						onChange={event => setSearchTerm(event.target.value)}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<Search fontSize="small" />
								</InputAdornment>
							),
						}}
					/>
					<Button variant="outlined" startIcon={<FilterList />}>
						{i18n.t("connections.extras.filters")}
					</Button>
					{/* A pagina ja e so de administrador (rota com roles admin). */}
					<Button
						variant="outlined"
						startIcon={<NetworkCheck />}
						onClick={() => setConnectivityTestOpen(true)}
					>
						Testar conexões
					</Button>
					<Button
						variant="contained"
						color="primary"
						startIcon={<Add />}
						onClick={handleOpenWhatsAppModal}
					>
						{i18n.t("connections.extras.add")}
					</Button>
				</div>
				<ConnectivityTestDialog
					open={connectivityTestOpen}
					onClose={() => setConnectivityTestOpen(false)}
				/>
			</div>

			<div className={classes.tabsRow}>
				<button
					type="button"
					className={`${classes.tabItem} ${
						connectionsTab === "active" ? classes.tabItemActive : ""
					}`}
					onClick={() => setConnectionsTab("active")}
				>
					{i18n.t("connections.extras.activeTab")} <span className={classes.countBubble}>{activeWhatsApps.length}</span>
				</button>
				<button
					type="button"
					className={`${classes.tabItem} ${
						connectionsTab === "archived" ? classes.tabItemActive : ""
					}`}
					onClick={() => setConnectionsTab("archived")}
				>
					{i18n.t("connections.extras.archivedTab")} <span className={classes.countBubble}>{archivedWhatsApps.length}</span>
				</button>
			</div>

			<Grid container spacing={2} className={classes.connectionCardsGrid}>
				{loading ? (
					<Grid item xs={12}>
						<Box display="flex" justifyContent="center" p={4}>
							<CircularProgress size={28} />
						</Box>
					</Grid>
				) : visibleWhatsApps.length > 0 ? (
					visibleWhatsApps.map(whatsApp => {
						const visual = getConnectionVisual(whatsApp);
						const Icon = visual.icon || WhatsApp;
						const isArchived = Boolean(whatsApp.archivedAt);
						const connectDisabled =
							isArchived ||
							isConnectionConnected(whatsApp) ||
							normalizeStatus(whatsApp?.status) === "OPENING";
						return (
							<Grid item xs={12} sm={6} md={4} key={whatsApp.id}>
								<div className={classes.activeConnectionCard}>
									<div className={classes.activeConnectionHeader}>
										<div className={classes.activeConnectionTitle}>
											<div
												className={classes.channelIcon}
												style={{ background: visual.color || "#0f766e", marginBottom: 0 }}
											>
												<Icon />
											</div>
											<div>
												<Typography variant="subtitle1">{whatsApp.name}</Typography>
												<Typography className={classes.channelDescription}>
													ID #{whatsApp.id} - {getConnectionTypeLabel(whatsApp)}
												</Typography>
											</div>
										</div>
										<div className={classes.activeConnectionHeaderActions}>
											<Button
												size="small"
												variant={connectDisabled ? "outlined" : "contained"}
												color="primary"
												className={`${classes.connectButton} ${
													isConnectionConnected(whatsApp)
														? classes.connectButtonConnected
														: ""
												}`}
												disabled={connectDisabled}
												onClick={() => handleConnectWhatsApp(whatsApp)}
											>
												{getConnectLabel(whatsApp)}
											</Button>
											<IconButton
												size="small"
												onClick={event => handleOpenConnectionMenu(event, whatsApp)}
											>
												<MoreVert fontSize="small" />
											</IconButton>
										</div>
									</div>
									<div className={classes.activeConnectionBody}>
										<div className={classes.cardMetaRow}>
											<CropFree fontSize="small" />
											<span className={classes.cardMetaText}>
												{i18n.t("connections.extras.connectionId", { id: whatsApp.id })}
											</span>
										</div>
										<div className={classes.cardMetaRow}>
											<Phone fontSize="small" />
											<span className={classes.cardMetaText}>
												{getConnectionPhone(whatsApp)}
											</span>
										</div>
										<div className={classes.cardMetaRow}>
											<Business fontSize="small" />
											<span className={classes.cardMetaText}>
												{whatsApp.defaultQueue?.name || i18n.t("connections.extras.noDefaultQueue")}
											</span>
										</div>
										<div className={classes.statusPill}>
											<span
												className={classes.statusDot}
												style={{
													background:
														isConnectionConnected(whatsApp)
															? "#22c55e"
															: "#fb7185",
												}}
											/>
											{getConnectionStatusLabel(whatsApp)}
										</div>
									</div>
								</div>
							</Grid>
						);
					})
				) : (
					<Grid item xs={12}>
						<Paper variant="outlined" style={{ padding: 24, textAlign: "center" }}>
							<Typography variant="subtitle1">
								{connectionsTab === "archived"
									? i18n.t("connections.extras.emptyArchived")
									: i18n.t("connections.extras.empty")}
							</Typography>
						</Paper>
					</Grid>
				)}
			</Grid>

			<div className={classes.paginationRow}>
				<span>{i18n.t("connections.extras.showingResults", { visible: visibleWhatsApps.length, total: filteredWhatsApps.length })}</span>
				<span>{i18n.t("connections.extras.pageLabel")}</span>
			</div>

			<Menu
				anchorEl={connectionMenuAnchor}
				open={Boolean(connectionMenuAnchor)}
				onClose={handleCloseConnectionMenu}
				classes={{ paper: classes.connectionMenuPaper }}
				keepMounted
			>
				<MenuItem
					disabled={
						!connectionMenuWhatsApp ||
						Boolean(connectionMenuWhatsApp?.archivedAt) ||
						!isQrCodeWhatsAppConnection(connectionMenuWhatsApp) ||
						normalizeStatus(connectionMenuWhatsApp?.status) === "CONNECTED" ||
						normalizeStatus(connectionMenuWhatsApp?.status) === "OPENING" ||
						connectionMenuWhatsApp?.status === "qrcode"
					}
					onClick={() => {
						const current = connectionMenuWhatsApp;
						handleCloseConnectionMenu();
						if (current) handleStartWhatsAppSession(current.id);
					}}
				>
					<ListItemIcon>
						<SyncAlt fontSize="small" />
					</ListItemIcon>
					<ListItemText primary={i18n.t("connections.extras.reconnect")} />
				</MenuItem>
				<MenuItem
					disabled={
						!connectionMenuWhatsApp ||
						Boolean(connectionMenuWhatsApp?.archivedAt) ||
						!isQrCodeWhatsAppConnection(connectionMenuWhatsApp) ||
						normalizeStatus(connectionMenuWhatsApp?.status) === "DISCONNECTED"
					}
					onClick={() => {
						const current = connectionMenuWhatsApp;
						handleCloseConnectionMenu();
						if (current) handleOpenConfirmationModal("disconnect", current.id);
					}}
				>
					<ListItemIcon>
						<PowerSettingsNew fontSize="small" />
					</ListItemIcon>
					<ListItemText primary={i18n.t("connections.buttons.disconnect")} />
				</MenuItem>
				<MenuItem
					onClick={() => {
						const current = connectionMenuWhatsApp;
						handleCloseConnectionMenu();
						if (current) handleEditWhatsApp(current);
					}}
				>
					<ListItemIcon>
						<Visibility fontSize="small" />
					</ListItemIcon>
					<ListItemText primary={i18n.t("connections.extras.view")} />
				</MenuItem>
				<MenuItem
					onClick={() => {
						const current = connectionMenuWhatsApp;
						handleCloseConnectionMenu();
						if (current) {
							handleOpenConfirmationModal(
								current.archivedAt ? "unarchive" : "archive",
								current.id
							);
						}
					}}
				>
					<ListItemIcon>
						{connectionMenuWhatsApp?.archivedAt ? (
							<UnarchiveOutlined fontSize="small" />
						) : (
							<ArchiveOutlined fontSize="small" />
						)}
					</ListItemIcon>
					<ListItemText
						primary={connectionMenuWhatsApp?.archivedAt ? i18n.t("connections.extras.unarchive") : i18n.t("connections.extras.archive")}
					/>
				</MenuItem>
				<MenuItem
					className={classes.connectionMenuDanger}
					onClick={() => {
						const current = connectionMenuWhatsApp;
						handleCloseConnectionMenu();
						if (current) handleOpenConfirmationModal("delete", current.id);
					}}
				>
					<ListItemIcon>
						<DeleteOutline fontSize="small" />
					</ListItemIcon>
					<ListItemText primary={i18n.t("connections.extras.delete")} />
				</MenuItem>
			</Menu>
		</div>
	);

	const renderConnectionCatalog = () => (
		<div className={classes.pageSurface}>
			<div className={classes.breadcrumb}>
				{i18n.t("connections.extras.breadcrumb")}
			</div>
			<div className={classes.addPageHeader}>
				<IconButton onClick={() => setScreenMode("list")}>
					<ArrowBack />
				</IconButton>
				<Typography variant="h4">{i18n.t("connections.extras.addConnectionTitle")}</Typography>
			</div>
			<div className={classes.addLayout}>
				{connectionOptionsLoading ? (
					<Box display="flex" justifyContent="center" p={4}>
						<CircularProgress size={28} />
					</Box>
				) : (
					<Grid container spacing={3} className={classes.connectionGrid}>
						{connectionOptions.map(option => {
							const isAvailable = option.state === "available" && option.supported;
							const canCreateConnection = canCreateConnectionFromOption(option);
							const isPlanIncluded = option.state !== "not_contracted";
							const visual = channelVisuals[option.key] || {};
							const Icon = visual.icon || Forum;
							const channelConnections = getConnectionsByChannel(option);
							const isExpanded = expandedChannelKey === option.key;
							const contractedButUnavailable =
								option.state === "coming_soon" || !option.supported;
							const statusLabel =
								option.state === "available"
									? option.available === null
										? i18n.t("connections.extras.available")
										: i18n.t("connections.extras.availableCount", { count: option.available })
									: option.state === "limit_reached"
									? i18n.t("connections.extras.limitReached")
									: "";
							return (
								<Grid item xs={12} sm={6} md={4} key={option.key}>
									<div
										className={`${classes.channelCard} ${
											isPlanIncluded ? "" : classes.channelCardDisabled
										}`}
										onClick={() => handleChannelCardClick(option)}
									>
										<div>
											<div className={classes.channelHeader}>
												<div
													className={`${classes.channelIcon} ${
														isPlanIncluded ? "" : classes.channelIconDisabled
													}`}
													style={{
														background: isPlanIncluded
															? visual.color || "#0f766e"
															: undefined,
													}}
												>
													<Icon />
												</div>
												{statusLabel ? (
													<span className={classes.channelStatus}>
														{option.state === "limit_reached" ? (
															<span className={classes.channelStatusDot} />
														) : null}
														{statusLabel}
													</span>
												) : null}
											</div>
											<Typography className={classes.channelName}>
												{option.name}
											</Typography>
											<Typography className={classes.channelDescription}>
												{channelConnections.length > 0
													? i18n.t(
															channelConnections.length > 1
																? "connections.extras.createdConnections"
																: "connections.extras.createdConnection",
															{ count: channelConnections.length }
													  )
													: contractedButUnavailable
													? i18n.t("connections.extras.contractToUse")
													: option.description}
											</Typography>
										</div>
										{isExpanded && channelConnections.length > 0 ? (
											<div className={classes.connectionList}>
												{channelConnections.map(connection => (
													<div
														key={connection.id}
														className={classes.connectionRow}
													>
														<span className={classes.connectionNameInline}>
															{connection.name}
														</span>
														<span>{connection.status || "-"}</span>
														<IconButton
															size="small"
															onClick={event => {
																event.stopPropagation();
																handleEditWhatsApp(connection);
															}}
														>
															<Edit fontSize="small" />
														</IconButton>
													</div>
												))}
											</div>
										) : null}
										<div className={classes.channelFooter}>
											<Typography className={classes.channelDescription}>
												{i18n.t("connections.extras.usage", { count: channelConnections.length, limit: option.limit ? `/${option.limit}` : "" })}
												
											</Typography>
											<Button
												size="small"
												color="primary"
												variant={canCreateConnection ? "contained" : "outlined"}
												className={classes.channelActionButton}
												onClick={event => {
													event.stopPropagation();
													handleSelectConnectionOption(option);
												}}
											>
												{canCreateConnection
													? i18n.t("connections.extras.newConnection")
													: option.state === "limit_reached"
													? i18n.t("connections.extras.limitReached")
													: i18n.t("connections.extras.request")}
											</Button>
										</div>
									</div>
								</Grid>
							);
						})}
					</Grid>
				)}
			</div>
		</div>
	);

	const renderBlockedChannelDialog = () => {
		const option = blockedChannelDialog.option || {};
		const titleByState = {
			limit_reached: i18n.t("connections.extras.blockedLimitTitle"),
			not_contracted: i18n.t("connections.extras.blockedNotContractedTitle"),
			coming_soon: i18n.t("connections.extras.blockedComingSoonTitle"),
		};
		const messageByState = {
			limit_reached:
				i18n.t("connections.extras.blockedLimitMessage"),
			not_contracted:
				i18n.t("connections.extras.blockedNotContractedMessage"),
			coming_soon:
				i18n.t("connections.extras.blockedComingSoonMessage"),
		};

		return (
			<Dialog
				open={blockedChannelDialog.open}
				onClose={handleCloseBlockedChannelDialog}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>
					{titleByState[option.state] || i18n.t("connections.extras.blockedUnavailable")}
				</DialogTitle>
				<DialogContent>
					<Typography className={classes.channelDescription}>
						{messageByState[option.state] || option.message}
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={handleCloseBlockedChannelDialog}
						color="secondary"
						variant="outlined"
					>
						{i18n.t("whatsappModal.buttons.cancel")}
					</Button>
					<Button
						onClick={handleContactSupport}
						color="primary"
						variant="contained"
					>
						{i18n.t("connections.extras.talkSupport")}
					</Button>
				</DialogActions>
			</Dialog>
		);
	};

	const handleOpenConfirmationModal = (action, whatsAppId) => {
		if (action === "disconnect") {
			setConfirmModalInfo({
				action: action,
				title: i18n.t("connections.confirmationModal.disconnectTitle"),
				message: i18n.t("connections.confirmationModal.disconnectMessage"),
				whatsAppId: whatsAppId,
			});
		}

		if (action === "delete") {
			setConfirmModalInfo({
				action: action,
				title: i18n.t("connections.confirmationModal.deleteTitle"),
				message: i18n.t("connections.confirmationModal.deleteMessage"),
				whatsAppId: whatsAppId,
			});
		}

		if (action === "archive") {
			setConfirmModalInfo({
				action: action,
				title: i18n.t("connections.extras.archiveTitle"),
				message:
					i18n.t("connections.extras.archiveMessage"),
				whatsAppId: whatsAppId,
			});
		}

		if (action === "unarchive") {
			setConfirmModalInfo({
				action: action,
				title: i18n.t("connections.extras.unarchiveTitle"),
				message:
					i18n.t("connections.extras.unarchiveMessage"),
				whatsAppId: whatsAppId,
			});
		}
		setConfirmModalOpen(true);
	};

	const handleSubmitConfirmationModal = async () => {
		if (confirmModalInfo.action === "disconnect") {
			try {
				await api.delete(`/whatsappsession/${confirmModalInfo.whatsAppId}`);
			} catch (err) {
				toastError(err);
			}
		}

		if (confirmModalInfo.action === "delete") {
			try {
				await api.delete(`/whatsapp/${confirmModalInfo.whatsAppId}`);
				toast.success(i18n.t("connections.toasts.deleted"));
			} catch (err) {
				toastError(err);
			}
		}

		if (confirmModalInfo.action === "archive") {
			try {
				await api.post(`/whatsapp/${confirmModalInfo.whatsAppId}/archive`);
				toast.success(i18n.t("connections.extras.archivedSuccess"));
			} catch (err) {
				toastError(err);
			}
		}

		if (confirmModalInfo.action === "unarchive") {
			try {
				await api.post(`/whatsapp/${confirmModalInfo.whatsAppId}/unarchive`);
				toast.success(i18n.t("connections.extras.restoredSuccess"));
			} catch (err) {
				toastError(err);
			}
		}

		setConfirmModalInfo(confirmationModalInitialState);
	};

	const renderActionButtons = whatsApp => {
		return (
			<>
		{normalizeStatus(whatsApp.status) === "QRCODE" && (
					<Button
						size="small"
						variant="contained"
						color="primary"
						onClick={() => handleOpenQrModal(whatsApp)}
					>
						{i18n.t("connections.buttons.qrcode")}
					</Button>
				)}
		{normalizeStatus(whatsApp.status) === "DISCONNECTED" && (
					<>
						<Button
							size="small"
							variant="outlined"
							color="primary"
							disabled={sessionActionLoadingId === whatsApp.id}
							onClick={() => handleStartWhatsAppSession(whatsApp.id)}
						>
							{i18n.t("connections.buttons.tryAgain")}
						</Button>{" "}
						<Button
							size="small"
							variant="outlined"
							color="secondary"
							disabled={sessionActionLoadingId === whatsApp.id}
							onClick={() => handleRequestNewQrCode(whatsApp.id)}
						>
							{i18n.t("connections.buttons.newQr")}
						</Button>
					</>
				)}
				{(normalizeStatus(whatsApp.status) === "CONNECTED" ||
					normalizeStatus(whatsApp.status) === "PAIRING" ||
					normalizeStatus(whatsApp.status) === "TIMEOUT") && (
					<Button
						size="small"
						variant="outlined"
						color="secondary"
						onClick={() => {
							handleOpenConfirmationModal("disconnect", whatsApp.id);
						}}
					>
						{i18n.t("connections.buttons.disconnect")}
					</Button>
				)}
				{isQrConnectionOpening(whatsApp) && (
					<Button size="small" variant="outlined" disabled color="default">
						{i18n.t("connections.buttons.connecting")}
					</Button>
				)}
			</>
		);
	};

	const renderStatusToolTips = whatsApp => {
		return (
			<div className={classes.customTableCell}>
				{normalizeStatus(whatsApp.status) === "DISCONNECTED" && (
					<CustomToolTip
						title={i18n.t("connections.toolTips.disconnected.title")}
						content={i18n.t("connections.toolTips.disconnected.content")}
					>
						<SignalCellularConnectedNoInternet0Bar color="secondary" />
					</CustomToolTip>
				)}
				{isQrConnectionOpening(whatsApp) && (
					<CircularProgress size={24} className={classes.buttonProgress} />
				)}
				{normalizeStatus(whatsApp.status) === "QRCODE" && (
					<CustomToolTip
						title={i18n.t("connections.toolTips.qrcode.title")}
						content={i18n.t("connections.toolTips.qrcode.content")}
					>
						<CropFree />
					</CustomToolTip>
				)}
				{normalizeStatus(whatsApp.status) === "CONNECTED" && (
					<CustomToolTip title={i18n.t("connections.toolTips.connected.title")}>
						<SignalCellular4Bar style={{ color: green[500] }} />
					</CustomToolTip>
				)}
				{(normalizeStatus(whatsApp.status) === "TIMEOUT" ||
					normalizeStatus(whatsApp.status) === "PAIRING") && (
					<CustomToolTip
						title={i18n.t("connections.toolTips.timeout.title")}
						content={i18n.t("connections.toolTips.timeout.content")}
					>
						<SignalCellularConnectedNoInternet2Bar color="secondary" />
					</CustomToolTip>
				)}
			</div>
		);
	};

	return (
		<MainContainer>
			<ConfirmationModal
				title={confirmModalInfo.title}
				open={confirmModalOpen}
				onClose={setConfirmModalOpen}
				onConfirm={handleSubmitConfirmationModal}
			>
				{confirmModalInfo.message}
			</ConfirmationModal>
			<QrcodeModal
				open={qrModalOpen}
				onClose={handleCloseQrModal}
				whatsAppId={!whatsAppModalOpen && selectedWhatsApp?.id}
			/>
			<WhatsAppModal
				open={whatsAppModalOpen}
				onClose={handleCloseWhatsAppModal}
				whatsAppId={!qrModalOpen && selectedWhatsApp?.id}
				initialConnectionType={selectedConnectionType}
				allowedConnectionTypes={connectionOptions
					.filter(
						option =>
							option.state === "available" &&
							option.supported &&
							supportedConnectionTypes.includes(option.key)
					)
					.map(option => option.key)}
			/>
			{renderBlockedChannelDialog()}
			{screenMode === "add" ? renderConnectionCatalog() : renderConnectionCards()}
			{false && <Paper className={classes.mainPaper} variant="outlined">
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell align="center">
								{i18n.t("connections.table.name")}
							</TableCell>
							<TableCell align="center">Grupos</TableCell>
							<TableCell align="center">
								{i18n.t("connections.table.status")}
							</TableCell>
							<TableCell align="center">
								{i18n.t("connections.table.session")}
							</TableCell>
							<TableCell align="center">
								{i18n.t("connections.table.lastUpdate")}
							</TableCell>
							<TableCell align="center">
								{i18n.t("connections.table.default")}
							</TableCell>
							<TableCell align="center">
								{i18n.t("connections.table.actions")}
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{loading ? (
							<TableRowSkeleton />
						) : (
							<>
								{whatsApps?.length > 0 &&
									whatsApps.map(whatsApp => (
										<TableRow key={whatsApp.id}>
											<TableCell align="center">{whatsApp.name}</TableCell>
											<TableCell align="center">
												{whatsApp.allowGroupMessages ? i18n.t("connections.extras.groupActive") : i18n.t("connections.extras.groupInactive")}
											</TableCell>
											<TableCell align="center">
												{renderStatusToolTips(whatsApp)}
											</TableCell>
											<TableCell align="center">
												{renderActionButtons(whatsApp)}
											</TableCell>
											<TableCell align="center">
												{format(parseISO(whatsApp.updatedAt), "dd/MM/yy HH:mm")}
											</TableCell>
											<TableCell align="center">
												{whatsApp.isDefault && (
													<div className={classes.customTableCell}>
														<CheckCircle style={{ color: green[500] }} />
													</div>
												)}
											</TableCell>
											<TableCell align="center">
												<IconButton
													size="small"
													onClick={() => handleEditWhatsApp(whatsApp)}
												>
													<Edit />
												</IconButton>

												<IconButton
													size="small"
													onClick={e => {
														handleOpenConfirmationModal("delete", whatsApp.id);
													}}
												>
													<DeleteOutline />
												</IconButton>
											</TableCell>
										</TableRow>
									))}
							</>
						)}
					</TableBody>
				</Table>
			</Paper>}
		</MainContainer>
	);
};

export default Connections;
